"use client";

import { mondes, merveilles, formatPrice } from "@/lib/data";
import { useCart, useUI } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, ShoppingBag } from "lucide-react";
import Image from "next/image";

const produitsPhares = [
  { id: "pha-s25u", name: "Galaxy S25 Ultra Titanium", price: 1299000, img: "/images/products/galaxy-s25-ultra.png" },
  { id: "pha-iphone15", name: "iPhone 15 Pro Max Or Rose", price: 1499000, img: "/images/products/iphone-15-pro-max.png" },
  { id: "pha-zfold6", name: "Galaxy Z Fold6 Limited", price: 1799000, img: "/images/products/galaxy-z-fold6.png" },
];

export function Mondes() {
  const openBoutique = useUI((s) => s.openBoutique);
  const addItem = useCart((s) => s.addItem);

  return (
    <section id="mondes" className="py-16 sm:py-20 bg-gradient-to-b from-white to-[#F5FBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold tracking-[0.3em] text-[#006B3F] uppercase mb-3">
            5 Univers Technologiques d&apos;Exception
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 tracking-tight">
            Les <span className="bg-gradient-to-r from-[#006B3F] to-[#008751] bg-clip-text text-transparent">5 Mondes</span>
          </h2>
          <div className="senegal-bar mx-auto mt-5" />
          <p className="mt-4 text-gray-500 leading-relaxed">
            Chaque monde est une boutique en ligne de luxe avec les meilleurs produits,
            les dernières nouveautés prisées par tous.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
          {mondes.map((monde, idx) => (
            <div
              key={monde.id}
              // IV) « Entrer dans la Boutique » : ouverture de la boutique du monde
              onClick={() => openBoutique(monde.id)}
              className={`group relative overflow-hidden rounded-3xl cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-1.5 ${
                idx === 0 ? "sm:col-span-2 lg:col-span-1" : ""
              }`}
            >
              <div className="relative aspect-[3/4]">
                <Image
                  src={monde.image}
                  alt={monde.name}
                  fill
                  className="object-cover group-hover:scale-110 transition-transform duration-700"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 20vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#04140D]/95 via-[#04140D]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h3 className="text-white font-black text-lg">{monde.name}</h3>
                  <p className="text-[#FFD700] text-xs font-medium mt-0.5">{monde.tagline}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {monde.features.slice(0, 3).map((f) => (
                      <span
                        key={f}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/80 border border-white/15"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#FFD700] group-hover:gap-3 transition-all">
                    Entrer dans la Boutique <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Produits phares sous Monde Mobile */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {produitsPhares.map((p) => (
            <div
              key={p.id}
              className="group flex items-center gap-4 p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-xl hover:border-[#006B3F]/20 transition-all"
            >
              <div className="relative w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-gray-50">
                <Image src={p.img} alt={p.name} fill className="object-cover group-hover:scale-105 transition-transform" sizes="80px" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold tracking-wider text-[#B8860B] uppercase">Produit Phare</p>
                <p className="font-bold text-gray-900 text-sm truncate">{p.name}</p>
                <p className="text-[#006B3F] font-black text-sm mt-0.5">
                  {formatPrice(p.price)} FCFA
                </p>
                <p className="text-[10px] text-gray-400">Platinum | Garantie 3 ans</p>
              </div>
              {/* V) Acheter : ajout direct au panier */}
              <Button
                size="sm"
                onClick={() =>
                  addItem({ id: p.id, name: p.name, price: p.price, image: p.img })
                }
                className="bg-[#006B3F] hover:bg-[#008751] text-white shrink-0"
              >
                <ShoppingBag className="w-3.5 h-3.5 mr-1" /> Acheter
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Merveilles() {
  const openMerveille = useUI((s) => s.openMerveille);

  return (
    <section id="merveilles" className="py-16 sm:py-20 bg-[#04140D] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(0,107,63,0.35), transparent), radial-gradient(ellipse 40% 30% at 80% 80%, rgba(255,215,0,0.08), transparent)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-xs font-bold tracking-[0.3em] text-[#FFD700] uppercase mb-3">
            7 Boutiques d&apos;Exception & Prestige
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
            Les{" "}
            <span className="bg-gradient-to-r from-[#FFD700] via-[#FFC83D] to-[#FFA500] bg-clip-text text-transparent">
              7 Merveilles
            </span>
          </h2>
          <p className="mt-4 text-white/60 leading-relaxed">
            Sept univers de rêve où l&apos;on désire tout avoir tant par la{" "}
            <span className="text-[#FFD700]">beauté</span>, le{" "}
            <span className="text-[#FFD700]">prestige</span> et le{" "}
            <span className="text-[#FFD700]">luxe</span>.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {merveilles.map((m, idx) => (
            <div
              key={m.id}
              className={`group relative overflow-hidden rounded-3xl border border-[#FFD700]/15 bg-white/[0.03] hover:border-[#FFD700]/40 transition-all duration-500 hover:-translate-y-1 ${
                idx === 6 ? "sm:col-span-2 lg:col-span-1" : ""
              }`}
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <Image
                  src={m.image}
                  alt={m.name}
                  fill
                  className="object-cover group-hover:scale-108 transition-transform duration-700"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#04140D] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-[10px] font-black tracking-widest bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black shadow-lg">
                  {m.badge}
                </span>
              </div>
              <div className="p-5">
                <p className="text-[10px] font-bold tracking-[0.25em] text-[#00B86B] uppercase">{m.category}</p>
                <h3 className="text-xl font-black text-white mt-1">{m.name}</h3>
                <p className="text-white/50 text-sm mt-0.5">{m.tagline}</p>
                <div className="mt-4 space-y-2">
                  {m.products.map((p) => (
                    <div
                      key={p.name}
                      className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-white/[0.05] border border-[#FFD700]/10 hover:border-[#FFD700]/30 transition-colors"
                    >
                      <span className="text-white/80 truncate pr-2">{p.name}</span>
                      <span className="text-[#FFD700] font-bold whitespace-nowrap text-xs">
                        {formatPrice(p.price)} F
                      </span>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  // VI) Explorer : ouverture du détail de la Merveille avec sa sélection
                  onClick={() => openMerveille(m.id)}
                  className="mt-4 w-full bg-transparent border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700] hover:text-black hover:border-[#FFD700] font-bold"
                >
                  <Sparkles className="w-4 h-4" /> Explorer
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
